import { useToggle } from '@dealstack/frontend/shared/components/ExpandablePanel/useToggle';
import { useForm } from 'react-hook-form';
import {
	CreateVestingTermGradedDto,
	UpdateVestingTermGradedDto,
	VestingTermGradedRes,
	VestingTermTimeUnitType,
} from '@dealstack/shared/lib/generated';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	MAX_MONTHLY_PERIOD_GRADED,
	MAX_YEARLY_PERIOD,
} from '@dealstack/shared/lib/common/business/vestingTerms.constants';

export const useGradedTermController = (
	vestingTermId: string,
	setGradedTermCreatorOpen: (bool: boolean) => void,
	onCreateVestingTermGraded: (vestingTermGraded: CreateVestingTermGradedDto) => void,
	onUpdateVestingTermGraded: (
		gradedTermId: string,
		formValues: UpdateVestingTermGradedDto
	) => void,
	remainingPercentage: number,
	gradedTerm?: VestingTermGradedRes
) => {
	const tCommonUi = useTranslationCommonUi();

	const { isExpanded, toggleExpanded } = useToggle();

	const gradedFormShape = useMemo(
		() =>
			z
				.object({
					percentage: z
						.number({
							invalid_type_error: tCommonUi(
								'vesting_terms.error.graded.percentage.number'
							),
							required_error: tCommonUi(
								'vesting_terms.error.graded.percentage.required'
							),
						})
						.positive(tCommonUi('vesting_terms.error.graded.percentage.positive'))
						.max(
							remainingPercentage + (gradedTerm?.percentage ?? 0),
							tCommonUi('vesting_terms.error.graded.percentage.max')
						),
					duration: z
						.number({
							invalid_type_error: tCommonUi(
								'vesting_terms.error.graded.duration.number'
							),
							required_error: tCommonUi(
								'vesting_terms.error.graded.duration.required'
							),
						})
						.int(tCommonUi('vesting_terms.error.graded.duration.int'))
						.positive(tCommonUi('vesting_terms.error.graded.duration.positive')),
					durationTimeUnit: z.nativeEnum(VestingTermTimeUnitType),
				})
				.refine(
					(data) => {
						if (
							data.durationTimeUnit === VestingTermTimeUnitType.MONTHLY &&
							data.duration > MAX_MONTHLY_PERIOD_GRADED
						) {
							return false;
						}
						if (
							data.durationTimeUnit === VestingTermTimeUnitType.YEARLY &&
							data.duration > MAX_YEARLY_PERIOD
						) {
							return false;
						}
						return true;
					},
					(data) => ({
						message: tCommonUi('vesting_terms.error.graded.duration.max_exceeded', {
							maxValue:
								data.durationTimeUnit === VestingTermTimeUnitType.MONTHLY
									? MAX_MONTHLY_PERIOD_GRADED
									: MAX_YEARLY_PERIOD,
							timeUnit: data.durationTimeUnit.toLocaleString(),
						}),
						path: ['duration'],
					})
				),
		[remainingPercentage, tCommonUi, gradedTerm]
	);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm({
		mode: 'all',
		defaultValues: {
			percentage: gradedTerm?.percentage ?? 0,
			duration: gradedTerm?.duration ?? 1,
			durationTimeUnit: gradedTerm?.durationTimeUnit ?? VestingTermTimeUnitType.MONTHLY,
		},
		resolver: zodResolver(gradedFormShape),
	});

	const onSubmit = (data: UpdateVestingTermGradedDto) => {
		if (gradedTerm) {
			onUpdateVestingTermGraded(gradedTerm.id, data);

			if (isExpanded) {
				toggleExpanded();
			}
		} else {
			onCreateVestingTermGraded({ ...data, vestingTermId });
			setGradedTermCreatorOpen(false);
		}
	};

	return {
		control,
		errors,
		toggleExpanded,
		handleSubmit,
		isExpanded,
		onSubmit,
	};
};
